import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { CoachRoleForm, EMPTY_ENTRY } from "../components/coach-input/CoachRoleForm";
import { useRoleLists } from "../lib/roleLists";
import { useSkillLibrary } from "../lib/inventorySkills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/coach-input`;

// Public, token-gated coach form. Shows only the player's name; expires after 30 days; one submission per link.
export default function CoachInputPage() {
  const { token } = useParams();
  const lists = useRoleLists(); const skills = useSkillLibrary();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${token}`).then(r => { setData(r.data); setForm(f => ({ ...f, ...Object.fromEntries(Object.entries(r.data.prefill || {}).filter(([, v]) => v)) })); })
      .catch(e => setError(e?.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try { await axios.post(`${API}/${token}`, { ...form, coachEmail: form.coachEmail || null }); setDone(true); }
    catch (e) { setError(e?.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  const ready = data && lists.ready && skills.ready;
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" style={{ "--org-accent": "#d8b854" }}>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] font-bold">Maximize Analytics · Coach Input</div>
        {error && <div className="mt-6 border border-white/10 bg-[#141414] p-6 text-sm text-[#c0c0c0]" data-testid="coach-input-error">{error}</div>}
        {!error && !ready && <div className="mt-6 text-sm text-[#757575]">Loading…</div>}
        {!error && ready && done && (
          <div className="mt-6 border border-white/10 bg-[#141414] p-8 text-center" data-testid="coach-input-thanks">
            <CheckCircle2 className="w-10 h-10 text-[#4ade80] mx-auto mb-3" />
            <h1 className="font-heading uppercase text-2xl">Thank you</h1>
            <p className="text-sm text-[#a0a0a0] mt-2">Your answers help us train {data.player.name} for exactly what your team needs.</p>
          </div>
        )}
        {!error && ready && !done && (
          <>
            <h1 className="font-heading uppercase text-2xl sm:text-3xl mt-3 leading-tight" data-testid="coach-input-heading">Help us understand {data.player.name}'s role on your team.</h1>
            <p className="text-sm text-[#a0a0a0] mt-2 mb-8">This helps us train them for exactly what you need. About 3 minutes.</p>
            <CoachRoleForm value={form} onChange={setForm} onSubmit={submit} submitting={busy} />
          </>
        )}
      </div>
    </div>
  );
}
